import { ReloadOutlined } from '@ant-design/icons';
import type { SatisfactionRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Rate,
  Segmented,
  Space,
  Statistic,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../session';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

const EVAL_TYPE_META: Record<string, string> = {
  DELIVERY: '配送时效',
  MATERIAL: '商品质量',
  AFTER_SALE: '售后处理',
  PLATFORM: '平台体验'
};

const STATUS_META: Record<string, { text: string; color: string }> = {
  SUBMITTED: { text: '待回复', color: 'gold' },
  REPLIED: { text: '已回复', color: 'green' },
  CLOSED: { text: '已关闭', color: 'default' }
};

const FILTERS = [
  { label: '全部', value: 'ALL' },
  { label: '待回复', value: 'SUBMITTED' },
  { label: '已回复', value: 'REPLIED' }
];

/**
 * 履约评价（供应商端）：只看「评价本方主体」的记录。
 * 归属由后端按令牌绑定的 supplier_id 强制裁剪（`ServiceSatisfactionService`），前端不传 supplierId。
 * 供应商可回复说明（整改/申诉），回复走同一线程落库，院内客服能看到。
 */
export default function EvaluationSupplier() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [replyTarget, setReplyTarget] = useState<SatisfactionRow | null>(null);
  const [replyForm] = Form.useForm<{ replyContent: string }>();

  const listQuery = useInfiniteQuery({
    queryKey: ['supplier', 'evaluation', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.satisfaction.list({
        lastId: pageParam ?? undefined,
        size: PAGE_SIZE,
        status: status === 'ALL' ? undefined : status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const stats = useQuery({
    queryKey: ['supplier', 'evaluation', 'stats'],
    queryFn: () => hq.satisfaction.stats()
  });

  const reply = useMutation({
    mutationFn: (values: { id: string; replyContent: string }) => hq.satisfaction.reply(values.id, values.replyContent),
    onSuccess: () => {
      message.success('回复已提交');
      setReplyTarget(null);
      replyForm.resetFields();
      void queryClient.invalidateQueries({ queryKey: ['supplier', 'evaluation'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '回复失败')
  });

  const rows = (listQuery.data?.pages ?? []).flatMap((page) => page.items);
  const summary = listQuery.data?.pages?.[0]?.summary;

  const columns: ColumnsType<SatisfactionRow> = [
    {
      title: '评价类型',
      dataIndex: 'evalType',
      width: 110,
      render: (value: string) => EVAL_TYPE_META[value] ?? value
    },
    { title: '业务单号', dataIndex: 'bizNo', width: 180, render: (value: string | null) => value || '-' },
    {
      title: '评分',
      dataIndex: 'scoreTotal',
      width: 140,
      render: (value: number) => <Rate disabled value={value} style={{ fontSize: 14 }} />
    },
    { title: '评价科室', dataIndex: 'deptName', width: 140, render: (value: string | null) => value || '-' },
    {
      title: '评价内容',
      dataIndex: 'content',
      ellipsis: { showTitle: false },
      render: ellipsisCell('content'),
      width: 360
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '评价时间',
      dataIndex: 'evalAt',
      width: 160,
      render: (value: string) => formatDisplayTime(value)
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_value, row) => (
        <Space size={4}>
          <Button type="link" onClick={() => setCurrentId(row.id)}>
            详情
          </Button>
          <Button
            type="link"
            disabled={row.status === 'CLOSED'}
            onClick={() => {
              replyForm.resetFields();
              setReplyTarget(row);
            }}
          >
            回复
          </Button>
        </Space>
      )
    }
  ];

  // 明细直接复用列表行：列表已含全部字段，没必要为「详情」新开接口
  const detailRow = rows.find((row) => row.id === currentId) ?? null;

  return (
    <Card
      title="履约评价"
      extra={
        <Button
          icon={<ReloadOutlined />}
          loading={listQuery.isFetching}
          onClick={() => void listQuery.refetch()}
        >
          刷新
        </Button>
      }
    >
      <Space size={24} wrap style={{ marginBottom: 12 }}>
        <Statistic title="评价总数" value={stats.data?.count ?? 0} />
        <Statistic title="平均分" value={stats.data?.avgScore ?? 0} precision={2} suffix="/ 5" />
        <Statistic
          title="好评率"
          value={Math.round((stats.data?.goodRate ?? 0) * 100)}
          suffix="%"
        />
        <Statistic title="回复率" value={Math.round((stats.data?.replyRate ?? 0) * 100)} suffix="%" />
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="这里只显示采购方对「贵司」的配送/商品/售后评价；回复会同步到院内客服会话，作为整改与申诉记录。"
      />

      <Space style={{ marginBottom: 12 }} wrap>
        <Segmented value={status} onChange={(value) => setStatus(String(value))} options={FILTERS} />
        {summary ? (
          <Typography.Text type="secondary">
            本页 {summary.count} 条，平均 {summary.avgScore} 分，好评率 {Math.round(summary.goodRate * 100)}%
          </Typography.Text>
        ) : null}
      </Space>

      <Table<SatisfactionRow>
        rowKey="id"
        size="small"
        loading={listQuery.isPending}
        columns={columns}
        dataSource={rows}
        scroll={{ x: sumWidth(columns) }}
        pagination={false}
      />

      <Space style={{ marginTop: 12 }}>
        <Button
          disabled={!listQuery.hasNextPage}
          loading={listQuery.isFetchingNextPage}
          onClick={() => void listQuery.fetchNextPage()}
        >
          加载更多
        </Button>
      </Space>

      <Drawer
        width={520}
        open={Boolean(currentId)}
        onClose={() => setCurrentId(null)}
        title="评价详情"
        destroyOnHidden
      >
        {detailRow ? (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="评价类型">
              {EVAL_TYPE_META[detailRow.evalType] ?? detailRow.evalType}
            </Descriptions.Item>
            <Descriptions.Item label="业务单号">{detailRow.bizNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="评分">
              <Rate disabled value={detailRow.scoreTotal} style={{ fontSize: 14 }} />
            </Descriptions.Item>
            <Descriptions.Item label="评价科室">{detailRow.deptName || '-'}</Descriptions.Item>
            <Descriptions.Item label="评价人">
              {detailRow.anonFlag ? '匿名' : detailRow.evalUserName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="评价时间">{formatDisplayTime(detailRow.evalAt)}</Descriptions.Item>
            <Descriptions.Item label="评价内容">{detailRow.content || '-'}</Descriptions.Item>
            <Descriptions.Item label="标签">{detailRow.tags || '-'}</Descriptions.Item>
            <Descriptions.Item label="我方回复">{detailRow.replyContent || '-'}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Typography.Text type="secondary">未找到该评价（可能已刷新，请重新打开列表）</Typography.Text>
        )}
      </Drawer>

      <Modal
        title="回复评价"
        open={Boolean(replyTarget)}
        onCancel={() => setReplyTarget(null)}
        onOk={() => replyForm.submit()}
        confirmLoading={reply.isPending}
        okText="提交回复"
        destroyOnHidden
      >
        <Form
          form={replyForm}
          layout="vertical"
          onFinish={(values) => reply.mutate({ id: replyTarget?.id ?? '', replyContent: values.replyContent })}
        >
          <Typography.Paragraph type="secondary">
            {replyTarget ? `${EVAL_TYPE_META[replyTarget.evalType] ?? replyTarget.evalType} · ${replyTarget.bizNo || '无单号'}` : ''}
          </Typography.Paragraph>
          <Form.Item
            name="replyContent"
            label="回复内容"
            rules={[
              { required: true, message: '请填写回复内容' },
              { min: 2, message: '至少 2 个字' },
              { max: 1000, message: '最多 1000 字' }
            ]}
          >
            <Input.TextArea rows={4} maxLength={1000} showCount placeholder="说明整改措施 / 申诉理由" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
